import React, { useCallback, useEffect, useMemo, useState } from "react";
import VizSensor from "react-visibility-sensor";

import brandOfferEntityMapper from "../../utils/brandOfferEntityMapper";
import { SNOWPLOW_SCHEMA_DATA_KEY } from "../../utils/snowplowEventMigrator";
import useTrack, { eventTypes } from "../../hooks/useTrack";
import windowRedirect from "../../utils/windowRedirect";
import copyClipboard from "../../utils/copyClipboard";
import useImpression from "../../hooks/useImpression";
import { TRACKED_TARGET } from "../../hooks/useCachedViewTrack";
import brandEventPropertyMapper from "../../utils/brandEventPropertyMapper";
import useData from "../../hooks/useData";
import getParsedRedirectUrl from "../../utils/getParsedRedirectUrl";
import { BRAND_CLICK_TARGET } from "../../event_conf";
import { OFFER_TYPE } from "../../conf";
import FlippableOfferCard, { REDIRECT_DELAY } from "./FlippableOfferCard";

const OfferRowCard = ({
	product,
	idx,
	parentBrand,
	parentBrandRef,
	isParentBrandInView,
	onDisplayTrack,
	onThresholdDisplayTrack,
}) => {
	const track = useTrack();
	const [inView, setInView] = useState(false);
	const [containerRef, setContainerRef] = useState(null);

	const { feedHasUpsell } = useData();

	const {
		id: brandId,
		incentive_type: incentiveType,
		incentive_landing_page_url: incentiveLandingPageUrl,
		incentive_code: incentiveCode,
	} = parentBrand;

	const defaultEventProps = useMemo(
		() => ({
			...brandEventPropertyMapper({
				brand: parentBrand,
				idx,
				feedHasUpsell,
			}),
			initiatingTarget: BRAND_CLICK_TARGET.BOTTOM_SLOT,
			[SNOWPLOW_SCHEMA_DATA_KEY]: {
				...brandOfferEntityMapper({
					brand: parentBrand,
				}),
			},
			brand_view_order: idx, // manual override to stay consistent with legacy events
		}),
		[parentBrand, feedHasUpsell, idx]
	);

	const redirectUrl = getParsedRedirectUrl({
		brand: parentBrand,
		redirectUrl: incentiveLandingPageUrl,
	});

	const redirectToBrand = () => {
		windowRedirect({
			url: redirectUrl,
		});
	};

	const copyAndRedirect = () => {
		if (incentiveType === OFFER_TYPE.PROMO_CODE)
			copyClipboard(incentiveCode);
		redirectToBrand();
	};

	const handleClick = (isFlipped) => {
		track(eventTypes.CLICK_BRAND, {
			...defaultEventProps,
			brand_redirect_link: redirectUrl,
			isInternalClick: false,
		});

		if (incentiveType === OFFER_TYPE.PROMO_CODE) {
			// We don't want to delay the redirect if the offer has already been claimed.
			// If `isFlipped` is true, that means the user has been redirected before.

			const redirectInMs = !isFlipped ? REDIRECT_DELAY : 0;

			setTimeout(() => {
				copyAndRedirect();
			}, redirectInMs);
			return;
		}

		redirectToBrand();
	};

	const handleVizChange = useCallback(
		(isVisible) => setInView(isVisible),
		[]
	);

	useEffect(() => {
		if (inView && isParentBrandInView && onDisplayTrack) {
			onDisplayTrack();
		}
	}, [inView, isParentBrandInView, onDisplayTrack]);

	const viewTrackBrandId = useMemo(
		() => `${brandId}_bottom_slot_dummy`,
		[brandId]
	);

	useImpression({
		id: viewTrackBrandId,
		cacheKey: TRACKED_TARGET.VIEWABLE_THRESHOLD_BRAND,
		event: "dummy_event",
		target: containerRef,
		callback: onThresholdDisplayTrack,
	});

	if (!product || !parentBrandRef) {
		return null;
	}

	return (
		<VizSensor
			onChange={handleVizChange}
			containment={parentBrandRef}
			partialVisibility
			offset={{ right: 30 }}
		>
			<FlippableOfferCard
				product={product}
				brand={parentBrand}
				onClick={handleClick}
				setContainerRef={setContainerRef}
				trackableId={viewTrackBrandId}
			/>
		</VizSensor>
	);
};

export default OfferRowCard;
